import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Mail, Loader2, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth0 } from '@auth0/auth0-react'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { exigirConfiguracionEstudio } from '@/services/configuracionService'
import { useServicios } from '@/hooks/useServicios'
import { emailService } from '@/services/emailService'
import { useCambiarEstadoPresupuesto, useNombreArchivoPresupuesto } from '@/hooks/usePresupuestos'
import { generatePresupuestoPdf } from './generate-presupuesto-pdf'
import type { Presupuesto, PresupuestoItem } from '@/types/database'
import { formatCurrency } from '@/lib/utils'

interface EnviarPresupuestoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  presupuesto: Presupuesto | null
}

export function EnviarPresupuestoDialog({
  open,
  onOpenChange,
  presupuesto,
}: EnviarPresupuestoDialogProps) {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId, organizacion } = useOrganizacion()
  const { data: configEstudioData, isError: configEstudioError } = useConfiguracionEstudio()
  const { data: servicios = [] } = useServicios()
  const cambiarEstado = useCambiarEstadoPresupuesto()
  // ORR-120: adjunto `Presupuesto-{Apellido}.pdf` (mismo nombre que la descarga)
  const nombreArchivo = useNombreArchivoPresupuesto(presupuesto)

  const [email, setEmail] = useState('')
  const [asunto, setAsunto] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [enviando, setEnviando] = useState(false)

  // ORR-253: la precarga vivía en el `onOpenChange` del Dialog y nunca corría.
  // Radix solo llama a ese callback cuando el cambio nace ADENTRO (Escape, click
  // afuera); los dos callers abren el diálogo cambiando la prop `open`, que no lo
  // dispara. Resultado: email, asunto y mensaje quedaban vacíos aunque el dato
  // estuviera guardado, y el botón Enviar quedaba deshabilitado por el email.
  // Va en un efecto sobre `open` — que es la señal real de apertura.
  useEffect(() => {
    if (!open || !presupuesto) return
    setEmail(presupuesto.cliente_email || '')
    setAsunto(`Presupuesto — ${organizacion?.nombre || 'Estudio Contable'}`)
    setMensaje(
      `Adjunto encontrará el presupuesto por un total de ${formatCurrency(presupuesto.total || 0)}.\n\nQuedamos a disposición para cualquier consulta.`
    )
    // `presupuesto` es un useState del caller, no la referencia de una query: su
    // identidad no cambia mientras el diálogo está abierto, así que el efecto no
    // vuelve a correr y no pisa lo que el usuario haya editado.
  }, [open, presupuesto, organizacion?.nombre])

  const handleEnviar = async () => {
    if (!presupuesto || !email) return
    if (!organizacionId) {
      toast.error('No se pudo identificar la organización. Recargá la página.')
      return
    }

    setEnviando(true)
    try {
      const configEstudio = exigirConfiguracionEstudio({
        data: configEstudioData,
        isError: configEstudioError,
      })

      // 1. Generate PDF
      const items = (presupuesto.items as unknown as PresupuestoItem[]) || []
      const doc = await generatePresupuestoPdf({
        presupuesto,
        items,
        servicios,
        configEstudio,
        organizacion,
      })

      // 2. Convert to base64
      const dataUri = doc.output('datauristring')
      const pdfBase64 = dataUri.split(',')[1]
      if (!pdfBase64) throw new Error('Error generando PDF')
      const filename = nombreArchivo

      // 3. Get auth token
      const token = await getAccessTokenSilently()

      // 4. Send email with PDF attachment
      const result = await emailService.enviarPresupuesto(
        token,
        email,
        {
          clienteNombre: presupuesto.cliente_nombre || 'Cliente',
          numeroPresupuesto: presupuesto.numero_presupuesto || 0,
          total: presupuesto.total || 0,
          fechaValidez: presupuesto.fecha_validez,
          mensajePersonalizado: mensaje,
          customSubject: asunto,
          whatsappEstudio: configEstudio?.telefono || undefined,
        },
        {
          organizacionId,
          clienteId: presupuesto.cliente_id ?? undefined,
          attachments: [{ filename, content: pdfBase64 }],
        }
      )

      if (!result.success) {
        throw new Error(result.error || 'Error enviando email')
      }

      // 5. Change estado to 'enviado'
      await cambiarEstado.mutateAsync({
        id: presupuesto.id,
        estado: 'enviado',
      })

      toast.success('Presupuesto enviado por email', {
        description: `Se envió a ${email}`,
      })
      onOpenChange(false)
    } catch (err) {
      console.error('Error enviando presupuesto:', err)
      toast.error(err instanceof Error ? err.message : 'Error al enviar presupuesto')
    } finally {
      setEnviando(false)
    }
  }

  if (!presupuesto) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Enviar Presupuesto #{presupuesto.numero_presupuesto}
          </DialogTitle>
          <DialogDescription>
            Se enviará el presupuesto en PDF al cliente por email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Email del cliente *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cliente@email.com"
            />
          </div>

          <div className="space-y-2">
            <Label>Asunto</Label>
            <Input
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Mensaje</Label>
            <Textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Paperclip className="h-4 w-4" />
            Presupuesto-{presupuesto.numero_presupuesto}.pdf (adjunto automático)
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={handleEnviar} disabled={enviando || !email}>
            {enviando ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-2" />
            )}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
